import {
  CurrencyToken,
  currencyTokenRenamedSchema,
  CurrencyTokenType
} from '@apeiron/currency'
import { OrbitalTrackState } from '@apeiron/orbital'
import { agDataGridNodeSchema } from '@src/deserialize/yup/agDataGrid'
import { planetLiteSchema } from '@src/deserialize/yup/planet'
import { starLiteSchema, starSuperLiteSchema } from '@src/deserialize/yup/star'
import {
  OrbitalTrackCompleteDetachDetail,
  OrbitalTrackDetachDetail,
  OrbitalTrackDetachDetailInputParams,
  OrbitalTrackDetail,
  OrbitalTrackLite,
  OrbitalTrackSetting,
  PublicStarRental,
  PublicStarRentalLite
} from '@src/types/orbitalTrack'
import * as yup from 'yup'

const orbitalTrackSettingSchema: yup.SchemaOf<OrbitalTrackSetting> = yup
  .object({
    canBeRenew: yup.boolean().default(false),
    currencyType: yup
      .mixed<CurrencyTokenType>()
      .oneOf(Object.values(CurrencyTokenType))
      .default(CurrencyTokenType.Unspecified),
    groupName: yup.string().default('0'),
    inviteAddressList: yup.array().of(yup.string()).default([]).nullable(),
    inviteGroupId: yup.string().default('0'),
    maxCountForEachAddress: yup.number().default(0),
    occupiedTrack: yup.number().default(0),
    priority: yup.number().default(0),
    rentalDuration: yup.number().default(0),
    rentalFee: yup.string().default('0'),
    totalTrack: yup.number().default(0)
  })
  .from('currencyCode', 'currencyType')
  .noUnknown(true)

export const orbitalTrackLiteSchema: yup.SchemaOf<OrbitalTrackLite> = yup
  .object({
    availableTrackTime: yup.string().default('0'),
    firstRentalStartTime: yup.string().default('0'),
    isOwnerAttaching: yup.boolean().default(false),
    planet: planetLiteSchema.nullable().default(null),
    planetId: yup.string().default('0'),
    planetOwnerAddress: yup.string().default('0'),
    renewable: yup.boolean().default(false),
    rentalFee: yup.number().default(0),
    rentalEndTime: yup.string().default('0'),
    rentalFeeAfterPlatform: yup.number().default(0),
    rentalStartTime: yup.string().default('0'),
    setting: orbitalTrackSettingSchema,
    slotIndex: yup.number().default(0),
    star: starLiteSchema.nullable().default(null),
    starId: yup.string().default('0'),
    state: yup.mixed().oneOf(Object.values(OrbitalTrackState)),
    tier: yup.number().default(0),
    totalAgingBuff: yup.number().default(0),
    totalTrackCount: yup.number().default(0)
  })
  .concat(agDataGridNodeSchema('attachedPlanetId'))
  .from('attachedPlanet', 'planet')
  .from('attachedPlanetId', 'planetId')
  .from('originalRentalStartTime', 'firstRentalStartTime')
  .from('starID', 'starId')
  .noUnknown(true)

export const orbitalTrackDetailSchema: yup.SchemaOf<OrbitalTrackDetail> = yup
  .object({
    availableOrbitalTrackCount: yup.number().default(0),
    inviteSettings: yup.array().of(orbitalTrackSettingSchema).default([]),
    openSetting: orbitalTrackSettingSchema.nullable(),
    ownerTracks: yup.array().of(orbitalTrackLiteSchema).default([]),
    star: starLiteSchema.nullable().default(null),
    totalOrbitalTrackCount: yup.number().default(0),
    tracks: yup.array().of(orbitalTrackLiteSchema).default([])
  })
  .noUnknown(true)

export const orbitalTrackDetachDetailSchema: yup.SchemaOf<OrbitalTrackDetachDetail> =
  yup
    .object({
      attachTime: yup.string().default('0'),
      canBeRenew: yup.boolean().default(false),
      contractEndTime: yup.string().default('0'),
      currencyType: yup
        .mixed<CurrencyTokenType>()
        .oneOf(Object.values(CurrencyTokenType))
        .default(CurrencyTokenType.Unspecified),
      detachPlanet: planetLiteSchema.nullable(),
      penaltyPercent: yup.number().default(0),
      planetOwnerRefundAmount: yup.string().default('0'),
      planetOwnerRefundPercent: yup.number().default(0),
      platformFee: yup.string().default('0'),
      rentalDuration: yup.number().default(0),
      rentalFee: yup.string().default('0'),
      rentalFeeAfterPlatform: yup.string().default('0'),
      rentalTimePercent: yup.number().default(0),
      starOwnerRentalFeeAmount: yup.string().default('0'),
      starOwnerRentalFeePercent: yup.number().default(0)
    })
    .from('currencyCode', 'currencyType')
    .noUnknown(true)

export const orbitalTrackDetachInputParamSchema: yup.SchemaOf<OrbitalTrackDetachDetailInputParams> =
  yup
    .object({
      starID: yup.string().default('0'),
      slotIndex: yup.number().default(0),
      planetID: yup.string().default('0'),
      originalRentalStartTime: yup.string().default('0'),
      isDetachByStarOwner: yup.boolean().default(false)
    })
    .noUnknown(true)

export const orbitalTrackCompleteDetachDetailSchema: yup.SchemaOf<OrbitalTrackCompleteDetachDetail> =
  yup
    .object({
      inputParam: orbitalTrackDetachInputParamSchema.nullable(),
      detachDetail: orbitalTrackDetachDetailSchema.nullable(),
      err: yup.string().nullable()
    })
    .noUnknown(true)

export const publicStarRentalLiteSchema: yup.SchemaOf<PublicStarRentalLite> =
  yup
    .object({
      agingBuff: yup.number().default(0),
      currencyType: yup
        .mixed<CurrencyTokenType>()
        .oneOf(Object.values(CurrencyTokenType))
        .default(CurrencyTokenType.Unspecified),
      duration: yup.number().default(0),
      freeTracks: yup.number().default(0),
      renewable: yup.boolean().default(false),
      rentalFee: yup.string().default('0'),
      rentedTracks: yup.number().default(0),
      star: starSuperLiteSchema
    })
    .from('availableTrackCount', 'freeTracks')
    .from('canBeRenew', 'renewable')
    .from('currencyCode', 'currencyType')
    .from('occupiedTrackCount', 'rentedTracks', true) // keep "occupiedTrackCount" for "publicStarRentalSchema" parsing
    .from('rentalDuration', 'duration')
    .from('totalAgingBuff', 'agingBuff')
    .noUnknown(true)

export const publicStarRentalSchema: yup.SchemaOf<PublicStarRental> =
  publicStarRentalLiteSchema
    .concat(
      yup.object({
        isOpenRentGroup: yup.boolean().default(false),
        occupiedTracks: yup.number().default(0),
        inviteGroupID: yup.string().default('0'),
        starID: yup.string().default('0')
      })
    )
    .from('occupiedTrackCount', 'occupiedTracks')
    .noUnknown(true)

export const obitalTrackSuggestCurrencySchema: yup.SchemaOf<CurrencyToken> =
  currencyTokenRenamedSchema
    .from('type.image', 'image')
    .from('type.imageLite', 'imageLite')
    .from('type.name', 'name')
    .from('type.type', 'type')
    .from('currencyCodeDecimal', 'decimals')
    .from('price', 'amount')
